import React, { useState, useEffect, useCallback } from 'react';
import { api, timeAgo } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { VerifiedBadge } from '../components/app/VerifiedBadge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import {
  Shield, Users, BadgeCheck, Flag, BarChart3, Search, MoreHorizontal,
  AlertTriangle, Ban, CheckCircle2, XCircle, Trash2, Eye, MonitorPlay,
} from 'lucide-react';
import { toast } from 'sonner';

function KpiCard({ label, value, icon: Icon, accent = 'primary' }) {
  const color = accent === 'blue' ? 'hsl(205,100%,56%)' : accent === 'amber' ? 'hsl(38,92%,55%)' : accent === 'red' ? 'hsl(0,72%,52%)' : 'hsl(142,92%,52%)';
  return (
    <div className="bg-card rounded-xl border border-border p-4 elev-1" data-testid="admin-kpi-card">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon size={15} style={{ color }} />
      </div>
      <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{value ?? '-'}</p>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    api.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
    api.get('/admin/analytics/timeseries').then((r) => setSeries(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total users" value={stats?.total_users} icon={Users} />
        <KpiCard label="Active today" value={stats?.active_today} icon={Eye} accent="blue" />
        <KpiCard label="Total posts" value={stats?.total_posts} icon={BarChart3} />
        <KpiCard label="Open reports" value={stats?.open_reports} icon={Flag} accent="red" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="AI questions today" value={stats?.ai_questions_today} icon={BarChart3} accent="blue" />
        <KpiCard label="Limit triggers today" value={stats?.limit_triggers_today} icon={AlertTriangle} accent="amber" />
        <KpiCard label="Rewarded unlocks" value={stats?.reward_unlocks_today} icon={MonitorPlay} accent="amber" />
        <KpiCard label="Pending verifications" value={stats?.pending_verifications} icon={BadgeCheck} accent="blue" />
      </div>

      {/* Ad engine monitor */}
      <div className="bg-card rounded-xl border border-border p-5 elev-1">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <MonitorPlay size={15} className="text-primary" /> Ad Engine Monitor
        </h3>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="text-center p-3 rounded-lg bg-secondary/50">
            <p className="text-xl font-bold text-primary">{stats?.ad_impressions ?? '-'}</p>
            <p className="text-[10px] text-muted-foreground uppercase">Impressions</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-secondary/50">
            <p className="text-xl font-bold text-[hsl(205,100%,56%)]">{stats?.ad_clicks ?? '-'}</p>
            <p className="text-[10px] text-muted-foreground uppercase">Clicks</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-secondary/50">
            <p className="text-xl font-bold text-[hsl(38,92%,55%)]">{stats?.ad_ctr ?? '-'}%</p>
            <p className="text-[10px] text-muted-foreground uppercase">CTR</p>
          </div>
        </div>
        <div className="h-56" data-testid="admin-analytics-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 10% 18%)" />
              <XAxis dataKey="date" stroke="hsl(215 12% 70%)" fontSize={11} />
              <YAxis stroke="hsl(215 12% 70%)" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'hsl(220 10% 9%)', border: '1px solid hsl(220 10% 18%)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="ai_questions" name="AI questions" stroke="hsl(205 100% 56%)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="ad_impressions" name="Ad impressions" stroke="hsl(142 92% 52%)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="posts" name="Posts" stroke="hsl(38 92% 55%)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [warnDialog, setWarnDialog] = useState(null);
  const [warnMsg, setWarnMsg] = useState('');
  const [banDialog, setBanDialog] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/users?search=${encodeURIComponent(search)}`).then((r) => setUsers(r.data)).catch(() => {});
  }, [search]);

  useEffect(() => {
    const tm = setTimeout(load, 300);
    return () => clearTimeout(tm);
  }, [load]);

  const warn = async () => {
    try {
      await api.post(`/admin/users/${warnDialog.id}/warn`, { message: warnMsg });
      toast.success(`Warning sent to @${warnDialog.username}`);
      setWarnDialog(null); setWarnMsg('');
      load();
    } catch { toast.error('Failed'); }
  };

  const ban = async (type, days = 7) => {
    try {
      await api.post(`/admin/users/${banDialog.id}/ban`, { ban_type: type, days });
      toast.success(`@${banDialog.username} banned (${type})`);
      setBanDialog(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const unban = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unban`);
      toast.success(`@${u.username} unbanned`);
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="bg-card rounded-xl border border-border elev-1 overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="relative">
          <Search size={15} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-users-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users by name, username or email..." className="ps-9" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="admin-users-table">
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>XP</TableHead>
              <TableHead>Warnings</TableHead>
              <TableHead>Reports</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-end">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid="admin-user-row">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="w-8 h-8"><AvatarImage src={u.avatar} /><AvatarFallback>{u.username?.[0]}</AvatarFallback></Avatar>
                    <div>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium">{u.full_name}</span>
                        {u.verified && <VerifiedBadge size={13} />}
                      </div>
                      <span className="text-xs text-muted-foreground">@{u.username}</span>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                <TableCell className="text-sm">{u.xp}</TableCell>
                <TableCell><Badge variant={u.warnings?.length ? 'destructive' : 'secondary'} className="text-[10px]">{u.warnings?.length || 0}</Badge></TableCell>
                <TableCell><Badge variant={u.report_count ? 'destructive' : 'secondary'} className="text-[10px]">{u.report_count || 0}</Badge></TableCell>
                <TableCell>
                  {u.banned
                    ? <Badge variant="destructive" className="text-[10px]">{u.ban_until ? `Banned until ${u.ban_until.slice(0, 10)}` : 'Permanent ban'}</Badge>
                    : <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">Active</Badge>}
                </TableCell>
                <TableCell className="text-end">
                  {u.role !== 'admin' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid="admin-user-actions-button" className="p-1.5 rounded-lg hover:bg-secondary"><MoreHorizontal size={16} /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid="admin-warn-user-button" onClick={() => setWarnDialog(u)}>
                          <AlertTriangle size={13} className="me-2 text-[hsl(38,92%,55%)]" /> Issue warning
                        </DropdownMenuItem>
                        {u.banned ? (
                          <DropdownMenuItem data-testid="admin-unban-user-button" onClick={() => unban(u)}>
                            <CheckCircle2 size={13} className="me-2 text-primary" /> Unban
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem data-testid="admin-ban-user-button" onClick={() => setBanDialog(u)} className="text-destructive">
                            <Ban size={13} className="me-2" /> Ban user
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Warn dialog */}
      <Dialog open={!!warnDialog} onOpenChange={(v) => !v && setWarnDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Issue warning to @{warnDialog?.username}</DialogTitle>
            <DialogDescription>The user will receive an official warning notification.</DialogDescription>
          </DialogHeader>
          <Input data-testid="admin-warn-message-input" value={warnMsg} onChange={(e) => setWarnMsg(e.target.value)} placeholder="Warning reason..." />
          <Button data-testid="admin-warn-confirm-button" onClick={warn} disabled={!warnMsg.trim()} className="w-full">Send warning</Button>
        </DialogContent>
      </Dialog>

      {/* Ban dialog */}
      <Dialog open={!!banDialog} onOpenChange={(v) => !v && setBanDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ban @{banDialog?.username}</DialogTitle>
            <DialogDescription>Choose ban duration. The user will not be able to log in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button data-testid="admin-ban-7days-button" variant="outline" onClick={() => ban('temporary', 7)} className="w-full">Temporary — 7 days</Button>
            <Button data-testid="admin-ban-30days-button" variant="outline" onClick={() => ban('temporary', 30)} className="w-full">Temporary — 30 days</Button>
            <Button data-testid="admin-ban-permanent-button" variant="destructive" onClick={() => ban('permanent')} className="w-full">Permanent ban</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VerificationTab() {
  const [requests, setRequests] = useState([]);

  const load = () => api.get('/admin/verification-requests').then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/verification-requests/${id}/${action}`);
      toast.success(action === 'approve' ? 'Verified! Blue checkmark granted.' : 'Request denied');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="bg-card rounded-xl border border-border elev-1 overflow-hidden">
      <div className="overflow-x-auto">
        <Table data-testid="admin-verification-queue-table">
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Activity</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-end">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.id} data-testid="admin-verification-row">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="w-8 h-8"><AvatarImage src={r.user?.avatar} /><AvatarFallback>{r.user?.username?.[0]}</AvatarFallback></Avatar>
                    <div>
                      <p className="text-sm font-medium">{r.user?.full_name}</p>
                      <p className="text-xs text-muted-foreground">@{r.user?.username}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.user?.post_count ?? 0} posts · {r.user?.xp ?? 0} XP</TableCell>
                <TableCell className="text-xs max-w-[220px] truncate">{r.reason}</TableCell>
                <TableCell>
                  <Badge variant={r.status === 'pending' ? 'outline' : r.status === 'approved' ? 'default' : 'destructive'}
                    className={`text-[10px] ${r.status === 'pending' ? 'border-[hsl(38,92%,55%)]/50 text-[hsl(38,92%,55%)]' : ''}`}>
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-end">
                  {r.status === 'pending' && (
                    <div className="flex gap-1.5 justify-end">
                      <Button data-testid="admin-verification-approve-button" size="sm" onClick={() => act(r.id, 'approve')} className="h-8 text-xs bg-[hsl(205,100%,56%)] text-black hover:bg-[hsl(205,100%,62%)]">
                        <CheckCircle2 size={13} className="me-1" /> Approve
                      </Button>
                      <Button data-testid="admin-verification-deny-button" size="sm" variant="outline" onClick={() => act(r.id, 'deny')} className="h-8 text-xs border-destructive/40 text-destructive">
                        <XCircle size={13} className="me-1" /> Deny
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {requests.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No verification requests</p>}
      </div>
    </div>
  );
}

function ModerationTab() {
  const [reports, setReports] = useState([]);

  const load = () => api.get('/admin/reports').then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const resolve = async (id, action) => {
    try {
      await api.post(`/admin/reports/${id}/resolve?action=${action}`);
      toast.success(action === 'delete_content' ? 'Content deleted & author notified' : 'Report dismissed');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="bg-card rounded-xl border border-border elev-1 overflow-hidden">
      <div className="overflow-x-auto">
        <Table data-testid="admin-moderation-table">
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Content</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Reporter / Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-end">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.id} data-testid="admin-report-row">
                <TableCell><Badge variant="outline" className="text-[10px]">{r.content_type}</Badge></TableCell>
                <TableCell className="text-xs max-w-[200px] truncate">
                  {r.content ? (r.content.text || r.content.caption || r.content.full_name || '[media]') : <span className="text-muted-foreground italic">deleted</span>}
                </TableCell>
                <TableCell className="text-xs">{r.content_author ? `@${r.content_author.username}` : '-'}</TableCell>
                <TableCell className="text-xs">
                  <p>@{r.reporter?.username}</p>
                  <p className="text-muted-foreground">{r.reason}</p>
                </TableCell>
                <TableCell>
                  <Badge variant={r.status === 'open' ? 'destructive' : 'secondary'} className="text-[10px]">{r.status}</Badge>
                </TableCell>
                <TableCell className="text-end">
                  {r.status === 'open' && (
                    <div className="flex gap-1.5 justify-end">
                      <Button data-testid="admin-report-delete-content-button" size="sm" variant="destructive" onClick={() => resolve(r.id, 'delete_content')} className="h-8 text-xs">
                        <Trash2 size={13} className="me-1" /> Delete content
                      </Button>
                      <Button data-testid="admin-report-dismiss-button" size="sm" variant="outline" onClick={() => resolve(r.id, 'dismiss')} className="h-8 text-xs">
                        Dismiss
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {reports.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No reports — community is clean!</p>}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useApp();

  if (user?.role !== 'admin') {
    return (
      <div className="py-20 text-center">
        <Shield size={40} className="mx-auto mb-3 text-destructive opacity-60" />
        <p className="text-sm text-muted-foreground">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4">
      <div className="flex items-center gap-2 mb-5">
        <Shield size={20} className="text-[hsl(205,100%,56%)]" />
        <h1 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Admin Control Panel</h1>
        <Badge variant="outline" className="text-[10px] border-[hsl(205,100%,56%)]/50 text-[hsl(205,100%,56%)]">SUPER-ADMIN</Badge>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger data-testid="admin-tab-overview" value="overview" className="gap-1.5"><BarChart3 size={14} /> Analytics</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-users" value="users" className="gap-1.5"><Users size={14} /> Users</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-verification" value="verification" className="gap-1.5"><BadgeCheck size={14} /> Verification</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-moderation" value="moderation" className="gap-1.5"><Flag size={14} /> Moderation</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="verification"><VerificationTab /></TabsContent>
        <TabsContent value="moderation"><ModerationTab /></TabsContent>
      </Tabs>
    </div>
  );
}
